import { useEffect, useState } from 'react';
import {
    BarElement, CategoryScale, Chart as ChartJS, Legend,
    LinearScale, Tooltip as ChartTooltip
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import {
    Box, Button, Card, CardContent, Dialog, DialogActions, DialogContent,
    DialogTitle, FormControlLabel, IconButton, LinearProgress, Paper, Stack, Switch, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';

import {
    deleteCommercialSeason, getClientGeographyInsights, getClientProductInsights,
    getClientSeasonalityInsights, getCommercialSeasons, saveCommercialSeason,
    updateCommercialSeason
} from '../../../actions/admin/clients';
import { CHANNEL_LABELS, SOURCE_LABELS, dateText, money } from '../../../helpers/admin/clients/clientHelper';

ChartJS.register(CategoryScale, LinearScale, BarElement, ChartTooltip, Legend);

const DataTable = ({ columns, rows, empty = 'No hay información para el periodo.' }) => (
    <TableContainer component={Paper} variant="outlined">
        <Table size="small">
            <TableHead><TableRow>{columns.map(column => <TableCell key={column.label}>{column.label}</TableCell>)}</TableRow></TableHead>
            <TableBody>
                {rows.map((row, index) => <TableRow key={row.key || row._id || index}>{columns.map(column => <TableCell key={column.label}>{column.render ? column.render(row) : row[column.key]}</TableCell>)}</TableRow>)}
                {!rows.length && <TableRow><TableCell colSpan={columns.length} align="center" sx={{ py: 4, color: 'text.secondary' }}>{empty}</TableCell></TableRow>}
            </TableBody>
        </Table>
    </TableContainer>
);

const Loading = () => <LinearProgress sx={{ my: 2 }} />;
const ErrorState = () => <Typography color="error">No se pudo recuperar la información. Intenta nuevamente.</Typography>;
const monthLabels = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

const CalendarHeatmap = ({ rows }) => {
    const years = [...new Set(rows.map(row => row.period.slice(0, 4)))].sort();
    const values = new Map(rows.map(row => [row.period, row]));
    const max = Math.max(1, ...rows.map(row => Number(row.gross_sales || 0)));
    return <TableContainer component={Paper} variant="outlined"><Table size="small"><TableHead><TableRow><TableCell>Año</TableCell>{monthLabels.map(month => <TableCell key={month} align="center">{month}</TableCell>)}</TableRow></TableHead><TableBody>
        {years.map(year => <TableRow key={year}><TableCell>{year}</TableCell>{monthLabels.map((month, index) => {
            const row = values.get(`${year}-${String(index + 1).padStart(2, '0')}`);
            const intensity = Number(row?.gross_sales || 0) / max;
            return <Tooltip key={month} title={row ? `${row.orders} pedidos · ${money(row.gross_sales)}` : 'Sin ventas'}><TableCell align="center" sx={{ bgcolor: `rgba(4, 166, 225, ${0.06 + intensity * 0.8})`, color: intensity > 0.55 ? '#fff' : 'inherit', minWidth: 54 }}>{row?.orders || 0}</TableCell></Tooltip>;
        })}</TableRow>)}
    </TableBody></Table></TableContainer>;
};

export const ProductInsights = ({ filters }) => {
    const [data, setData] = useState(undefined);
    useEffect(() => { let active = true; setData(undefined); getClientProductInsights(filters).then(result => active && setData(result.status ? result : null)); return () => { active = false; }; }, [filters]);
    if (data === undefined) return <Loading />;
    if (data === null) return <ErrorState />;
    return <Stack spacing={3}>
        <Box><Typography variant="h6" sx={{ mb: 1 }}>Productos con mayor demanda y recompra</Typography>
            <DataTable rows={data.products || []} columns={[
                { label: 'Producto', render: row => [row.title, row.variant].filter(Boolean).join(' ') },
                { label: 'Unidades', key: 'units' },
                { label: 'Compradores', key: 'customers' },
                { label: 'Venta mercancía', render: row => money(row.merchandise_sales) },
                { label: 'Recompra', render: row => `${row.repeat_rate}%` },
                { label: 'Intervalo', render: row => row.average_repurchase_days === null ? '—' : `${row.average_repurchase_days} días` }
            ]} />
        </Box>
        <Box><Typography variant="h6" sx={{ mb: 1 }}>Productos comprados juntos</Typography>
            <DataTable rows={data.baskets || []} columns={[
                { label: 'Producto A', key: 'left' }, { label: 'Producto B', key: 'right' }, { label: 'Pedidos juntos', key: 'orders' }
            ]} />
        </Box>
    </Stack>;
};

export const GeographyInsights = ({ filters }) => {
    const [data, setData] = useState(undefined);
    useEffect(() => { let active = true; setData(undefined); getClientGeographyInsights(filters).then(result => active && setData(result.status ? result : null)); return () => { active = false; }; }, [filters]);
    if (data === undefined) return <Loading />;
    if (data === null) return <ErrorState />;
    const dimensionColumns = label => [
        { label, key: 'name' }, { label: 'Pedidos', key: 'orders' },
        { label: 'Clientes', key: 'customers' }, { label: 'Venta aprobada', render: row => money(row.gross_sales) }
    ];
    const named = (rows, labels) => (rows || []).map(row => ({ ...row, name: labels?.[row.key] || row.name || row.key }));
    return <Stack spacing={3}>
        <Box><Typography variant="h6" sx={{ mb: 1 }}>Canales de venta</Typography><DataTable rows={named(data.channels, CHANNEL_LABELS)} columns={dimensionColumns('Canal')} /></Box>
        <Box><Typography variant="h6" sx={{ mb: 1 }}>Sistemas fuente</Typography><DataTable rows={named(data.source_systems, SOURCE_LABELS)} columns={dimensionColumns('Sistema')} /></Box>
        <Box><Typography variant="h6" sx={{ mb: 1 }}>Sucursales</Typography><DataTable rows={named(data.branches)} columns={dimensionColumns('Sucursal')} /></Box>
        <Box><Typography variant="h6" sx={{ mb: 1 }}>Destinos principales</Typography><DataTable rows={named(data.states)} columns={dimensionColumns('Estado')} /></Box>
        <Box><Typography variant="h6" sx={{ mb: 1 }}>Ciudades principales</Typography><DataTable rows={named(data.cities)} columns={dimensionColumns('Ciudad')} /></Box>
    </Stack>;
};

const emptySeason = { series_key: '', name: '', start_date: '', end_date: '', color: '#04A6E1', active: true };
const inputDate = value => value ? new Date(value).toLocaleDateString('en-CA', {
    timeZone: 'America/Mexico_City', year: 'numeric', month: '2-digit', day: '2-digit'
}) : '';

const SeasonManager = ({ open, onClose, onChanged }) => {
    const [seasons, setSeasons] = useState([]);
    const [form, setForm] = useState(emptySeason);
    const [saving, setSaving] = useState(false);
    const load = () => getCommercialSeasons().then(result => result.status && setSeasons(result.seasons || []));
    useEffect(() => { if (open) load(); }, [open]);
    const edit = season => setForm({
        _id: season._id, series_key: season.series_key, name: season.name,
        start_date: inputDate(season.start_date), end_date: inputDate(season.end_date),
        color: season.color || '#04A6E1', active: season.active !== false
    });
    const save = async () => {
        if (!form.series_key || !form.name || !form.start_date || !form.end_date) return;
        setSaving(true);
        const { _id, ...body } = form;
        const result = _id ? await updateCommercialSeason(_id, body) : await saveCommercialSeason(body);
        setSaving(false);
        if (result.status) { setForm(emptySeason); load(); onChanged(); }
    };
    const remove = async id => {
        if (!window.confirm('¿Eliminar esta temporada comercial?')) return;
        const result = await deleteCommercialSeason(id);
        if (result.status) { load(); onChanged(); }
    };
    return <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>Temporadas comerciales</DialogTitle>
        <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Pueden superponerse; sus resultados no deben sumarse entre sí.</Typography>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ mb: 2 }}>
                <TextField size="small" label="Serie" placeholder="buen-fin" value={form.series_key} onChange={event => setForm(current => ({ ...current, series_key: event.target.value }))} />
                <TextField size="small" label="Nombre" value={form.name} onChange={event => setForm(current => ({ ...current, name: event.target.value }))} />
                <TextField size="small" type="date" label="Inicio" InputLabelProps={{ shrink: true }} value={form.start_date} onChange={event => setForm(current => ({ ...current, start_date: event.target.value }))} />
                <TextField size="small" type="date" label="Fin" InputLabelProps={{ shrink: true }} value={form.end_date} onChange={event => setForm(current => ({ ...current, end_date: event.target.value }))} />
                <TextField size="small" type="color" label="Color" InputLabelProps={{ shrink: true }} value={form.color} onChange={event => setForm(current => ({ ...current, color: event.target.value }))} sx={{ width: 90 }} />
                <FormControlLabel control={<Switch checked={form.active} onChange={event => setForm(current => ({ ...current, active: event.target.checked }))} />} label="Activa" />
                <Button variant="contained" onClick={save} disabled={saving}>{form._id ? 'Actualizar' : 'Agregar'}</Button>
            </Stack>
            <DataTable rows={seasons} columns={[
                { label: 'Temporada', render: row => <Stack direction="row" spacing={1} alignItems="center"><Box sx={{ width: 12, height: 12, borderRadius: 1, bgcolor: row.color }} /> <span>{row.name}</span></Stack> },
                { label: 'Serie', key: 'series_key' },
                { label: 'Periodo', render: row => `${dateText(row.start_date)} – ${dateText(row.end_date)}` },
                { label: 'Estado', render: row => row.active ? 'Activa' : 'Inactiva' },
                { label: 'Acciones', render: row => <><Tooltip title="Editar"><IconButton size="small" onClick={() => edit(row)}><EditIcon fontSize="small" /></IconButton></Tooltip><Tooltip title="Eliminar"><IconButton size="small" color="error" onClick={() => remove(row._id)}><DeleteIcon fontSize="small" /></IconButton></Tooltip></> }
            ]} />
        </DialogContent>
        <DialogActions><Button onClick={onClose}>Cerrar</Button></DialogActions>
    </Dialog>;
};

export const SeasonalityInsights = ({ filters }) => {
    const [data, setData] = useState(undefined);
    const [managerOpen, setManagerOpen] = useState(false);
    const [revision, setRevision] = useState(0);
    useEffect(() => { let active = true; setData(undefined); getClientSeasonalityInsights(filters).then(result => active && setData(result.status ? result : null)); return () => { active = false; }; }, [filters, revision]);
    if (data === undefined) return <Loading />;
    if (data === null) return <ErrorState />;
    const calendar = data.calendar || [];
    return <Stack spacing={3}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }}>
            <Box><Typography variant="h6">Temporalidad de compra</Typography><Typography variant="body2" color="text.secondary">Zona: {data.reporting_time_zone}</Typography></Box>
            <Button startIcon={<AddIcon />} variant="outlined" onClick={() => setManagerOpen(true)}>Administrar temporadas</Button>
        </Stack>
        <Card variant="outlined"><CardContent><Typography sx={{ mb: 2 }}>Ventas aprobadas por mes</Typography>
            <Box sx={{ height: 340 }}><Bar data={{ labels: calendar.map(row => row.period), datasets: [{ label: 'Venta aprobada', data: calendar.map(row => row.gross_sales), backgroundColor: '#04A6E1' }] }} options={{ responsive: true, maintainAspectRatio: false }} /></Box>
        </CardContent></Card>
        <Box><Typography variant="h6" sx={{ mb: 1 }}>Mapa de calor mensual</Typography><CalendarHeatmap rows={calendar} /></Box>
        <Box><Typography variant="h6" sx={{ mb: 1 }}>Temporadas comerciales</Typography><DataTable rows={data.commercial || []} columns={[
            { label: 'Temporada', key: 'name' }, { label: 'Periodo', render: row => `${dateText(row.start_date)} – ${dateText(row.end_date)}` },
            { label: 'Pedidos', key: 'orders' }, { label: 'Clientes', key: 'customers' },
            { label: 'Venta aprobada', render: row => money(row.gross_sales) }, { label: 'Ticket promedio', render: row => money(row.average_order_value) },
            { label: 'Vs. edición anterior', render: row => row.comparison?.gross_sales_change_percent === null || row.comparison?.gross_sales_change_percent === undefined ? '—' : `${row.comparison.gross_sales_change_percent > 0 ? '+' : ''}${row.comparison.gross_sales_change_percent}%` }
        ]} /></Box>
        <SeasonManager open={managerOpen} onClose={() => setManagerOpen(false)} onChanged={() => setRevision(value => value + 1)} />
    </Stack>;
};
